import React, { useState, useEffect, useContext } from 'react';
import {
  checkInRegistrant,
  deleteRegistrant,
  fetchRegistrantsByEventId,
  fetchRegistrantsByOrganizationIdAndEventId,
  fetchUserRegistrantsByEventId,
  markAsAttending,
  markAsInvited,
  markAsNotAttending,
  markAsNotInvited,
  postRankings,
  unCheckInRegistrant
} from '../services/registrantService';
import { EnrichedRegistrant, FileAnswer, InstrumentAnswer, RegistrantRanking } from '../types/Registrant';
import { EventContext } from '../context/EventContext';
import { getRegistrantFileUrl } from '../services/registrantFileService';
import InstrumentDropdown from './InstrumentDropdown';
import { EventInstrument } from '../types/Instrument';
import { getEventInstruments } from '../services/eventInstrumentService';
import JudgeRegistrantPopup from './JudgeRegistrantPopup';
import { Evaluation, Scorecard } from '../types/Scorecard';
import { getScorecardsByEventId } from '../services/scorecardService';
import { UserRoleContext } from '../context/UserRoleContext';
import { getEvaluationsByEventId } from '../services/evaluationService';
import EvaluationPopup from './EvaluationPopup';
import ToggleSwitch from './ToggleSwitch';
import ExportRegistrantsPopup from './ExportRegistrantsPopup';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { ROLES } from '../constants/roles';
import { ROUTES } from '../constants/routes';

const RegistrantList: React.FC = () => {
  const [registrants, setRegistrants] = useState<EnrichedRegistrant[]>([]);
  const [registrantToDelete, setRegistrantToDelete] = useState<EnrichedRegistrant>();
  const [loading, setLoading] = useState<boolean>(false);
  const { currentEvent } = useContext(EventContext);
  const { userRole } = useContext(UserRoleContext);

  const [availableInstruments, setAvailableInstruments] = useState<EventInstrument[]>([]);
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [showJudgePopup, setShowJudgePopup] = useState<boolean>(false);
  const [ignoreScore, setIgnoreScore] = useState<boolean>(false);
  const [showScores, setShowScores] = useState<boolean>(true);
  const [scorecards, setScorecards] = useState<Scorecard[]>([]);
  const [currentScorecard, setCurrentScorecard] = useState<Scorecard | null>(null);
  const [currentJudgedRegistrant, setCurrentJudgedRegistrant] = useState<EnrichedRegistrant | null>(null);
  const [evaluationAverages, setEvaluationAverages] = useState<Record<string, number>>({});
  const [groupedEvaluations, setGroupedEvaluations] = useState<Record<string, Evaluation[]>>({});
  const [hideNotInvited, setHideNotInvited] = useState<boolean>(false);
  const [hideNotAttending, setHideNotAttending] = useState<boolean>(false);
  const [hideNotCheckedIn, setHideNotCheckedIn] = useState<boolean>(false);
  const [showExportDataPopup, setShowExportDataPopup] = useState<boolean>(false);
  const [showDeleteRegistrantPopup, setShowDeleteRegistrantPopup] = useState<boolean>(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [adjustRankings, setAdjustRankings] = useState<boolean>(false);

  const [selectedRegistrant, setSelectedRegistrant] = useState<EnrichedRegistrant | null>(null);

  const [evaluationPopupOpen, setEvaluationPopupOpen] = useState(false);

  const [confirmationText, setConfirmationText] = useState('');


  const handleViewEvaluationsClick = (registrant: EnrichedRegistrant) => {
    setSelectedRegistrant(registrant);
    setEvaluationPopupOpen(true);
  };

  const closeEvaluationsPopup = () => {
    setEvaluationPopupOpen(false);
    setSelectedRegistrant(null);
  };

  const handleToggleIgnoreScore = () => {
    setIgnoreScore(!ignoreScore);
  }

  const handleSetCurrentScorecard = (scorecard: Scorecard) => {
    setCurrentScorecard(scorecard);
  }

  const [showPaid, setShowPaid] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [showActions, setShowActions] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'first_name',
    direction: 'asc',
  });

  useEffect(() => {
    const fetchScorecards = async () => {
      if (currentEvent) {
        const eventScorecards = await getScorecardsByEventId(currentEvent.event_id);
        setScorecards(eventScorecards);
        setDefaultScorecard(eventScorecards);
        if (eventScorecards.length == 0 && currentEvent.has_judging) {
          addToast('No scorecards available. Please ensure a scorecard has been created for this event.');
        }
      }
    };

    fetchScorecards();
  }, [currentEvent]);

  const presentJudgePopupForRegistrant = (registrant: EnrichedRegistrant) => {
    setCurrentJudgedRegistrant(registrant);
    setShowJudgePopup(true);
  }

  const setDefaultScorecard = (eventScorecards: any[]) => {
    const defaultScorecard = eventScorecards.find(scorecard => scorecard.is_default === 1);
    if (defaultScorecard) {
      setCurrentScorecard(defaultScorecard);
    }
    else if (eventScorecards.length == 1) {
      setCurrentScorecard(eventScorecards[0] as Scorecard);
    }
  };

  useEffect(() => {
    fetchRegistrantsAndEvaluations();
  }, [currentEvent]);

  const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
  };

  const fetchRegistrantsAndEvaluations = async () => {
    setLoading(true);

    try {
      if (currentEvent && userRole?.host_id == currentEvent.host_id) {
        const [registrantData, evaluationData] = await Promise.all([
          fetchRegistrantsByEventId(currentEvent.event_id),
          getEvaluationsByEventId(currentEvent.event_id),
        ]);
        // if (userRole?.role_id == ROLES.JUDGE) {
        //   const filteredRegistrants = registrantData.filter((registrant: { is_checked_in: boolean }) => registrant.is_checked_in);

        //   // Set the filtered registrants to state
        //   setRegistrants(filteredRegistrants);
        // } else {
        setRegistrants(registrantData);
        // }

        const evaluations = evaluationData.reduce(
          (acc: Record<string, Evaluation[]>, evalItem: Evaluation) => {
            if (!acc[evalItem.registrant_id]) {
              acc[evalItem.registrant_id] = [];
            }
            acc[evalItem.registrant_id].push(evalItem);
            return acc;
          },
          {}
        );
        setGroupedEvaluations(evaluations);
      } else {
        loadRegistrantsForOrganization();
      }
    } catch (error) {
      console.error('Error fetching registrants or evaluations:', error);
    }

    setLoading(false);
  };


  const loadRegistrantsForOrganization = async () => {

    try {
      if (currentEvent && userRole?.organization_id) {
        const [registrantData] = await Promise.all([
          fetchRegistrantsByOrganizationIdAndEventId(userRole?.organization_id, currentEvent.event_id),
        ]);
        setRegistrants(registrantData);

      } else if (currentEvent) {
          const [registrantData] = await Promise.all([
          fetchUserRegistrantsByEventId(currentEvent.event_id),
        ]);
        setRegistrants(registrantData);
      }
    } catch (error) {
      console.error('Error fetching registrants or evaluations:', error);
    }
    setLoading(false);

  }

  // useEffect(() => {
  //   const loadRegistrants = async () => {
  //     setLoading(true);
  //     if (currentEvent) {
  //       const data = await fetchRegistrantsByEventId(currentEvent.event_id);
  //       setRegistrants(data);
  //     }
  //     setLoading(false);
  //   };
  //   loadRegistrants();
  // }, [currentEvent]);

  useEffect(() => {
    const calculateAverages = () => {
      const averages = Object.entries(groupedEvaluations).reduce(
        (result: Record<string, number>, [registrantId, evaluations]) => {
          const byJudge = evaluations.reduce((judgeMap: Record<string, any[]>, evalItem: Evaluation) => {
            if (!judgeMap[evalItem.judge_id]) {
              judgeMap[evalItem.judge_id] = [];
            }
            judgeMap[evalItem.judge_id].push(evalItem);
            return judgeMap;
          }, {});

          const recentScores = Object.values(byJudge).map((judgeEvals: any[]) =>
            judgeEvals.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
              .total_score
          );

          const recentScoresNumbers = recentScores.map(score => parseFloat(score));

          const average =
            recentScoresNumbers.reduce((sum, score) => sum + score, 0) / recentScoresNumbers.length;
          result[registrantId] = average || 0;
          return result;
        },
        {}
      );
      setEvaluationAverages(averages);
    };

    calculateAverages();
  }, [groupedEvaluations]);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        if (currentEvent && currentEvent.event_type_id === 'MUSIC') {
          const instrumentData = await getEventInstruments(currentEvent.event_id);

          const sortedInstruments = [...instrumentData].sort(
            (a, b) => (a.display_order || 0) - (b.display_order || 0)
          );
          setAvailableInstruments(sortedInstruments);
        }
      } catch (error) {
        console.error('Error fetching instruments:', error);
      }
    };

    fetchInstruments();
  }, [currentEvent]);

  if (loading) return <div>Loading...</div>;

  const uniqueQuestions = Array.from(
    new Set(registrants.flatMap(r => r.answers.map(a => a.question_text)))
  );

  const uniqueCustomQuestions = Array.from(
    new Set(registrants.flatMap(r => r.custom_answers.map(ca => ca.question_text)))
  );

  const handleSelectionChange = (selected: string[]) => {
    setSelectedInstruments(selected);
  };

  const handleSort = (key: string) => {
    if (adjustRankings) {
      setSortConfig({ key: 'ranking', direction: 'asc' });
      return;
    }
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const handleJudgeClose = () => {
    setCurrentJudgedRegistrant(null);
    setShowJudgePopup(false);
    fetchRegistrantsAndEvaluations();

  }

  const presentPopupHandler = (registrant: EnrichedRegistrant) => {
    // setCurrentRegistrant(registrant);
    // setShowPopup(true);
  }

  const toggleBooleanField = async (
    registrantId: string,
    field: 'is_invited' | 'is_checked_in',// is_attending
    trueHandler: (id: string) => Promise<void>,
    falseHandler: (id: string) => Promise<void>
  ) => {
    const registrant = registrants.find(r => r.registrant_id === registrantId);
    if (!registrant) return;
    const updatedValue = !registrant[field];
    try {
      await (updatedValue ? trueHandler : falseHandler)(registrantId);
      setRegistrants(prev =>
        prev.map(r =>
          r.registrant_id === registrantId
            ? {
              ...r,
              [field]: updatedValue,
              // ...(field === 'is_invited' && !updatedValue ? { is_attending: false, is_checked_in: false } : {}),
              ...(field === 'is_invited' && !updatedValue ? { is_checked_in: false } : {})
            }
            : r
        )
      );
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    }
  };

  const getValue = (registrant: EnrichedRegistrant, key: string): string => {
    // First check if it's a direct property
    if (key in registrant) {
      const val = registrant[key as keyof EnrichedRegistrant];
      return typeof val === 'string' || typeof val === 'number' ? val.toString() : '';
    }

    // Otherwise, search in answers by question_text
    const answer = registrant.answers.find(a => a.question_text === key);
    if (!answer) return '';

    if (answer.type === 'instrument') {
      const inst = answer as InstrumentAnswer;
      return inst.answer.name;
    } else if (answer.type === 'file') {
      const file = answer as FileAnswer;
      return file.answer.file_name;
    } else {
      return answer.answer?.toString() ?? '';
    }
  };

  const sortedRegistrants = [...registrants].sort((a, b) => {
    const aVal = getValue(a, sortConfig.key);
    const bVal = getValue(b, sortConfig.key);

    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

    const aNum = parseFloat(aVal);
    const bNum = parseFloat(bVal);

    const isNumeric = !isNaN(aNum) && isFinite(aNum) && !isNaN(bNum) && isFinite(bNum);

    let comparison = 0;

    if (isNumeric) {
      comparison = aNum - bNum;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const moveUp = async (registrant: EnrichedRegistrant) => {
    if (!currentEvent) {
      addToast('No event!');
      return;
    }
    // Normalize rankings and sort
    const normalizedGroup = registrants
      .slice()
      .sort((a, b) => {
        if (a.ranking !== b.ranking) return (a.ranking ?? 0) - (b.ranking ?? 0);
        return a.last_name.localeCompare(b.last_name);
      })
      .map((r, idx) => ({ ...r, ranking: idx + 1 }));

    const index = normalizedGroup.findIndex(r => r.registrant_id === registrant.registrant_id);

    if (index <= 0) return; // Already at top

    // Move current registrant up by swapping with the one above
    const updatedGroup = [...normalizedGroup];
    const temp = updatedGroup[index - 1];
    updatedGroup[index - 1] = updatedGroup[index];
    updatedGroup[index] = temp;

    // Reassign proper rankings based on new order
    const reRanked = updatedGroup.map((r, idx) => ({
      ...r,
      ranking: idx + 1
    }));

    const formattedForAPI: RegistrantRanking[] = reRanked.map(r => ({
      registrant_id: r.registrant_id,
      ranking: r.ranking,
    }));

    try {
      const success = await postRankings(formattedForAPI);
      if (success) {
        setRegistrants(prev =>
          prev.map(r => {
            const updated = reRanked.find(u => u.registrant_id === r.registrant_id);
            return updated ? updated : r;
          })
        );
      } else {
        addToast('Error updating ranking', true);
      }
    } catch (error) {
      addToast('Error updating ranking', true);
      console.error('Failed to update rankings:', error);
    }
  };


  const moveDown = async (registrant: EnrichedRegistrant) => {
    if (!currentEvent) {
      addToast('No event!');
      return;
    }

    // Normalize rankings and sort
    const normalizedGroup = registrants
      .slice()
      .sort((a, b) => {
        if (a.ranking !== b.ranking) return (a.ranking ?? 0) - (b.ranking ?? 0);
        return a.last_name.localeCompare(b.last_name);
      })
      .map((r, idx) => ({ ...r, ranking: idx + 1 }));

    const index = normalizedGroup.findIndex(r => r.registrant_id === registrant.registrant_id);

    if (index === -1 || index >= normalizedGroup.length - 1) return; // Already at bottom

    // Swap current registrant with the one below
    const updatedGroup = [...normalizedGroup];
    const temp = updatedGroup[index + 1];
    updatedGroup[index + 1] = updatedGroup[index];
    updatedGroup[index] = temp;

    // Reassign proper rankings based on new order
    const reRanked = updatedGroup.map((r, idx) => ({
      ...r,
      ranking: idx + 1
    }));

    const formattedForAPI: RegistrantRanking[] = reRanked.map(r => ({
      registrant_id: r.registrant_id,
      ranking: r.ranking,
    }));

    try {
      const success = await postRankings(formattedForAPI);
      if (success) {
        setRegistrants(prev =>
          prev.map(r => {
            const updated = reRanked.find(u => u.registrant_id === r.registrant_id);
            return updated ? updated : r;
          })
        );
      } else {
        addToast('Error updating ranking', true);
      }
    } catch (error) {
      addToast('Error updating ranking', true);
      console.error('Failed to update rankings:', error);
    }
  };


  const exportDataPressed = () => {
    setShowExportDataPopup(true);
  };

  const fileUrlCache = new Map<string, { url: string, timestamp: number }>();
  const CACHE_TTL = 5 * 60 * 1000;

  const getCachedUrl = async (fileId: string, forceDownload = false): Promise<string | null> => {
    const cached = fileUrlCache.get(fileId);
    const now = Date.now();
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.url;
    }

    const url = await getRegistrantFileUrl(fileId, forceDownload);
    if (url) {
      fileUrlCache.set(fileId, { url, timestamp: now });
    }
    return url;
  };

  const DOWNLOAD_COOLDOWN = 2000; // 5 seconds
  let lastDownloadTime = 0;

  // Store downloaded file IDs in a Set for quick lookup
  const downloadedFiles = new Set<string>();

  const handleDownload = async (fileId: string, fileName: string) => {
    const now = Date.now();

    if (downloadedFiles.has(fileId)) {
      addToast('You have already downloaded this file. Refresh the page if you need to download it again.', true);
      return;
    }

    if (now - lastDownloadTime < DOWNLOAD_COOLDOWN) {
      addToast('Please wait before downloading again.', true);
      return;
    }

    lastDownloadTime = now;

    const url = await getCachedUrl(fileId, true);
    if (!url) return;

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    downloadedFiles.add(fileId);
  };

  const VIEW_COOLDOWN = 2000; // 2 seconds
  let lastViewTime = 0;

  const viewedFiles = new Set<string>();

  const handleView = async (fileId: string) => {
    const now = Date.now();

    if (viewedFiles.has(fileId)) {
      addToast('You have already viewed this file. Refresh the page if you need to view again.', true);
      return;
    }

    if (now - lastViewTime < VIEW_COOLDOWN) {
      addToast('Please wait before viewing again.', true);
      return;
    }

    lastViewTime = now;

    const url = await getCachedUrl(fileId, false); // false means view mode, not download
    if (!url) return;

    window.open(url, '_blank');

    viewedFiles.add(fileId);
  };

  const handleDeletePressed = async (registrantId: string | undefined) => {

    try {
      if (registrantId && registrantId != "") {
        const success = await deleteRegistrant(registrantId);
        if (success) {
          addToast('Registrant and data permanently deleted.');
          setRegistrants(registrants.filter(reg => reg.registrant_id !== registrantId));
        } else {
          addToast('Error deleting registrant', true);
        }
      } else {
        addToast('Unexpected error', true);
      }
    } catch (error) {
      addToast('Error deleting registrant, please try again.', true);
    }
    setShowDeleteRegistrantPopup(false);
    setConfirmationText('');
    setRegistrantToDelete(undefined);

  };


  const filteredRegistrants: EnrichedRegistrant[] = sortedRegistrants.filter(reg => {
    const search = searchText.toLowerCase();

    // Basic fields to search
    const basicFields = [
      reg.first_name ?? '',
      reg.last_name ?? '',
      reg.email ?? '',
      reg.phone_number ?? '',
      reg.organization_name ?? '',
      reg.city ?? '',
      reg.state ?? '',
      reg.group_name ?? '',
      reg.organization_contact_email ?? '',
      reg.organization_emergency_contact ?? '',
      reg.organization_emergency_phone ?? '',
      reg.organization_bookkeeper_name ?? '',
      reg.organization_bookkeeper_phone ?? '',
      String(reg.chair_number) ?? ''
    ];

    // Custom answer text values to search
    const customAnswers = reg.answers.map(ans => {
      if (ans.type === 'instrument') {
        const inst = ans as InstrumentAnswer;
        return inst.answer.name;
      } else if (ans.type === 'file') {
        const file = ans as FileAnswer;
        return file.answer.file_name;
      } else {
        return ans.answer?.toString() ?? '';
      }
    });

    const customQuestionAnswers = reg.custom_answers.map(ans => {
      return ans.answer_text?.toString() ?? '';
    });

    const matchesSearchText = [...basicFields, ...customAnswers, ...customQuestionAnswers].some(field =>
      field?.toLowerCase().includes(search)
    );
    if (hideNotInvited && !reg.is_invited) return false;
    // if (hideNotAttending && !reg.is_attending) return false;
    if (hideNotCheckedIn && !reg.is_checked_in) return false;

    if (currentEvent?.event_type_id === 'MUSIC') {
      const instrumentAnswers = reg.answers.filter(
        (answer) => answer.type === 'instrument'
      ) as InstrumentAnswer[];

      const hasInstrumentAnswer = instrumentAnswers.length > 0;

      const matchesInstrumentSelection =
        !hasInstrumentAnswer || // show if no instrument answer
        instrumentAnswers.some((instrumentAnswer) =>
          selectedInstruments.length === 0 ||
          selectedInstruments.includes(instrumentAnswer.answer.instrument_id)
        );
      return matchesSearchText && matchesInstrumentSelection;
    }

    return true;
  });

  return (
    <div>
      <div style={{ overflowX: 'auto', height: 'calc(100vh - 60px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', width: '100%', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <h2 style={{ minWidth: '300px' }}>{currentEvent?.event_name} Registrants</h2>

          <div className='card' style={{ display: 'flex', flexDirection: 'column', maxWidth: '900px', gap: '0px', padding: '10px' }}>
            <h3 style={{ marginBlockStart: '0em', marginBlockEnd: '0em' }}>Event Statistics</h3>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '10px' }}>
              {!!currentEvent?.has_invitation && <h4 style={{ marginBlockStart: '0.5em', marginBlockEnd: '0em' }}>{filteredRegistrants.filter(r => r.is_invited).length} Invited</h4>}
              {/* {!!currentEvent?.has_invitation && <h4 style={{ marginBlockStart: '0.5em', marginBlockEnd: '0em' }}>{filteredRegistrants.filter(r => r.is_attending).length} Attending</h4>} */}
              {!!currentEvent?.has_checkin && <h4 style={{ marginBlockStart: '0.5em', marginBlockEnd: '0em' }}>{filteredRegistrants.filter(r => r.is_checked_in).length} Checked In</h4>}
              <h4 style={{ marginBlockStart: '0.5em', marginBlockEnd: '0em' }}>{registrants.length} Total</h4>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ marginBottom: 5, padding: 10, width: '100%', minWidth: 200, maxWidth: 400 }}
          />
          {currentEvent?.event_type_id === 'MUSIC' && (
            <InstrumentDropdown
              availableInstruments={availableInstruments}
              selectedInstruments={selectedInstruments}
              onSelectionChange={handleSelectionChange}
            />
          )}
        </div>

        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'space-between' }}>

          <div className='card' style={{ display: 'flex', flexDirection: 'row', maxWidth: '1000px', gap: '5px', padding: '10px', paddingLeft: '15px', flexWrap: 'wrap'}}>
            {!!currentEvent?.has_invitation && (<ToggleSwitch
              label="Show Invited"
              checked={!hideNotInvited}
              onChange={() => { setHideNotInvited(!hideNotInvited) }}
            />)}
            {/* {!!currentEvent?.has_invitation && (<ToggleSwitch
              label="Show Attending"
              checked={!hideNotAttending}
              onChange={() => { setHideNotAttending(!hideNotAttending) }}
            />)} */}
            {!!currentEvent?.has_checkin && <ToggleSwitch
              label="Show Checked-In"
              checked={!hideNotCheckedIn}
              onChange={() => { setHideNotCheckedIn(!hideNotCheckedIn) }}
            />}
            <ToggleSwitch
              label="Show Details"
              checked={showDetails}
              onChange={setShowDetails}
            />

            {userRole?.role_id != 'JUDGE' && <ToggleSwitch
              label="Show Actions"
              checked={showActions}
              onChange={setShowActions}
            />}

            {/* {userRole?.role_id != ROLES.JUDGE && <ToggleSwitch
            label="Show Paid Status"
            checked={showPaid}
            onChange={setShowPaid}
          />} */}
          </div>

          <div style={{ flexDirection: 'row', display: 'flex', gap: '10px' }}>
            {/* {(userRole?.role_id == ROLES.ADMIN && userRole.organization_id != undefined) && <ToggleSwitch
              label="Adjust Registrant Rankings"
              checked={adjustRankings}
              onChange={() => { setAdjustRankings(!adjustRankings); handleSort('ranking'); }}
            />} */}
            <button onClick={exportDataPressed} style={{ maxHeight: '40px' }}>Export Data</button>
          </div>

        </div>

        <div className='tableWrapper' style={{ maxHeight: '600px' }}>
          <table>
            <thead>
              <tr>
                {(userRole?.role_id != 'JUDGE' && userRole?.role_id != 'BILLING') && <th>Edit</th>}
                <th onClick={() => handleSort('first_name')}>First Name</th>
                <th onClick={() => handleSort('last_name')}>Last Name</th>
                {!!currentEvent?.has_invitation && <th onClick={() => handleSort('is_invited')}>Invited</th>}
                {/* {!!currentEvent?.has_invitation && <th onClick={() => handleSort('is_attending')}>Attending</th>} */}
                {!!currentEvent?.has_checkin && <th onClick={() => handleSort('is_checked_in')}>Checked In</th>}
                {!!(currentEvent?.has_judging && ((userRole?.role_id == ROLES.JUDGE && userRole.host_id == currentEvent.host_id) || ((userRole?.role_id == ROLES.ADMIN && userRole?.host_id == currentEvent.host_id && showActions)))) && <th>Evaluate</th>}
                {!!(currentEvent?.has_judging && userRole?.host_id == currentEvent.host_id) && <th>View Scores</th>}
                <th onClick={() => handleSort('group_name')}>Group</th>
                {!!(currentEvent?.has_judging) && <th onClick={() => handleSort('chair_number')}>Chair</th>}
                <th onClick={() => handleSort('email')}>Email</th>
                <th onClick={() => handleSort('phone_number')}>Phone</th>
                {/* {!!currentEvent?.has_judging && <th onClick={() => handleSort('ranking')}>Ranking</th>} */}
                {showPaid && <th onClick={() => handleSort('paid')}>Paid</th>}
                {!!currentEvent?.require_organization && <th onClick={() => handleSort('organization_name')}>Organization</th>}
                {!!currentEvent?.require_organization && <th onClick={() => handleSort('city')}>City</th>}
                {!!currentEvent?.require_organization && <th onClick={() => handleSort('state')}>State</th>}
                {/* <th onClick={() => handleSort('organization_contact_email')}>Org Contact Email</th> */}
                {/* <th onClick={() => handleSort('organization_emergency_contact')}>Org Emergency Contact</th> */}
                {/* <th onClick={() => handleSort('organization_emergency_phone')}>Org Emergency Phone</th> */}
                {/* <th onClick={() => handleSort('organization_bookkeeper_name')}>Org Bookkeeper Name</th> */}
                {/* <th onClick={() => handleSort('organization_bookkeeper_phone')}>Org Bookkeeper Phone</th> */}

                {showDetails && uniqueQuestions.map(q => <th key={q} onClick={() => handleSort(q)}>{q}</th>)}
                {showDetails && uniqueCustomQuestions.map(q => <th key={q} onClick={() => handleSort(q)}>{q}</th>)}
                {(userRole?.role_id == 'ADMIN' || userRole?.role_id == 'MANAGER') && <th>Delete</th>}
              </tr>
            </thead>
            <tbody>
              {filteredRegistrants.map(reg => (
                <tr key={reg.registrant_id}>
                  {(userRole?.role_id != 'JUDGE' && userRole?.role_id != 'BILLING') && <td>
                    <button style={{ minWidth: '50px'}} onClick={() => { navigate(`${ROUTES.EDIT_REGISTRANT}/${reg.registrant_id}`); }} disabled={currentEvent?.is_locked}>Edit</button></td>}
                  <td>{reg.first_name}</td>
                  <td>{reg.last_name}</td>


                  {!!currentEvent?.has_invitation && <td style={{ maxWidth: '80px'}}>
                    {/* {showActions && ( */}
                    <ToggleSwitch
                      label={reg.is_invited ? 'Yes' : 'No'}
                      checked={reg.is_invited}
                      onChange={() => toggleBooleanField(reg.registrant_id, 'is_invited', markAsInvited, markAsNotInvited)}
                      hideToggle={!showActions || currentEvent.host_id != userRole?.host_id}
                      disabled={currentEvent.is_locked}
                    />
                    {/* )} */}
                  </td>}

                  {/* {!!currentEvent?.has_invitation && <td>
                    {reg.is_invited && (
                      <ToggleSwitch
                        label={reg.is_attending ? 'Yes' : 'No'}
                        checked={reg.is_attending}
                        onChange={() => toggleBooleanField(reg.registrant_id, 'is_attending', markAsAttending, markAsNotAttending)}
                        hideToggle={!showActions}
                        disabled={currentEvent.is_locked}
                      />
                    )}
                  </td>} */}

                  {!!currentEvent?.has_checkin && <td>
                    {reg.is_invited && (
                      <ToggleSwitch
                        label={reg.is_checked_in ? 'Yes' : 'No'}
                        checked={reg.is_checked_in}
                        onChange={() => toggleBooleanField(reg.registrant_id, 'is_checked_in', checkInRegistrant, unCheckInRegistrant)}
                        hideToggle={!showActions}
                        disabled={!currentEvent.checkin_open || currentEvent.is_locked}
                      />
                    )}
                  </td>}

                  {!!(currentEvent?.has_judging && ((userRole?.role_id == ROLES.JUDGE && userRole.host_id == currentEvent.host_id) || ((userRole?.role_id == ROLES.ADMIN && userRole.host_id == currentEvent.host_id && showActions)))) && <td>            <button
                    style={{ width: '100%' }}
                    onClick={() => presentJudgePopupForRegistrant(reg)}
                    disabled={currentEvent.is_locked}
                  >
                    Evaluate
                  </button></td>}

                  {!!(currentEvent?.has_judging && userRole?.host_id == currentEvent.host_id) && <td>              <button
                    style={{ width: '100%' }}
                    onClick={() => handleViewEvaluationsClick(reg)}
                  >
                    ({evaluationAverages[reg.registrant_id]?.toFixed(1) ?? 'None'}%) View
                  </button></td>}

                  

                  <td>{reg.group_name}</td>
                  {!!currentEvent?.has_judging && <td>{reg.chair_number}</td>}

                  <td>{reg.email}</td>
                  <td>{reg.phone_number}</td>
                  {/* {!!currentEvent?.has_judging && <td>
                    {adjustRankings && <div style={{ display: 'flex', gap: '2px' }}><button onClick={() => { moveUp(reg) }} disabled={reg.ranking == 1} style={{ padding: '3px 5px' }}>{'\u25B2'}</button>
                      <button onClick={() => { moveDown(reg) }} disabled={reg.ranking == filteredRegistrants.length} style={{ padding: '3px 5px' }}>{'\u25BC'}</button>
                    </div>}
                    {reg.ranking}
                  </td>} */}


                  {/* {showPaid && <td>{reg.is_invited ? (reg.paid ? 'Yes' : 'No') : ''}</td>} */}
                  {!!currentEvent?.require_organization && <td>{reg.organization_name}</td>}
                  {!!currentEvent?.require_organization && <td>{reg.city}</td>}
                  {!!currentEvent?.require_organization && <td>{reg.state}</td>}
                  {/* <td>{reg.organization_contact_email}</td> */}
                  {/* <td>{reg.organization_emergency_contact}</td> */}
                  {/* <td>{reg.organization_emergency_phone}</td> */}
                  {/* <td>{reg.organization_bookkeeper_name}</td> */}
                  {/* <td>{reg.organization_bookkeeper_phone}</td> */}

                  {showDetails &&
                    uniqueQuestions.map(q => {
                      const answer = reg.answers.find(a => a.question_text === q);
                      if (!answer) return <td key={q}></td>;

                      let content: React.ReactNode = '';
                      switch (answer.type) {
                        case 'instrument':
                          const instrumentAnswer = answer as InstrumentAnswer;
                          content = instrumentAnswer.answer.name;
                          break;
                        case 'file':
                        case 'file':
                          const fileAnswer = answer as FileAnswer;
                          const fileId = fileAnswer.answer.registrant_file_id;
                          const fileName = fileAnswer.answer.file_name;

                          content = (
                            <span>
                              {fileName && (
                                <div style={{ display: 'flex', flexDirection: 'row', gap: '2px'}}>
                                  <button onClick={() => handleView(fileId)}>
                                    View
                                  </button>
                                  <button onClick={() => handleDownload(fileId, fileName)}>Download</button>
                                </div>
                              )}
                            </span>
                          );
                          break;

                          break;
                        default:
                          content = String(answer.answer ?? '-');
                          break;
                      }
                      return <td key={q}>{content}</td>;
                    })}

                  {showDetails && uniqueCustomQuestions.map(q => {
                    const answer = reg.custom_answers.find(a => a.question_text === q);
                    if (!answer) return <td key={q}></td>;
                    let content: React.ReactNode = '';
                    content = String(answer.answer_text ?? '');
                    return <td key={q}>{content}</td>;
                  })}
                  {(userRole?.role_id == 'ADMIN' || userRole?.role_id == 'MANAGER') && <td>
                    <button type='button' className='warningButton' 
                    style={{ padding: '8px', paddingTop: '6px', paddingBottom: '6px'}} onClick={() => {
                    setRegistrantToDelete(reg);
                    setShowDeleteRegistrantPopup(true);
                  }}>Delete</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showJudgePopup && currentJudgedRegistrant && (
        <div className="popOverContainer"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            padding: '20px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            zIndex: 1000,
          }}>
          <JudgeRegistrantPopup
            scorecards={scorecards}
            registrant={currentJudgedRegistrant}
            closePopup={handleJudgeClose}
            setCurrentScorecard={handleSetCurrentScorecard}
            scorecard={currentScorecard}
            ignoreScore={ignoreScore}
            toggleIgnoreScore={handleToggleIgnoreScore} />
        </div>
      )}
      {showJudgePopup && currentJudgedRegistrant && (
        <div className="popOverBackdrop"
        />)}

      <EvaluationPopup
        isOpen={evaluationPopupOpen}
        onClose={closeEvaluationsPopup}
        registrant={selectedRegistrant}
        evaluations={groupedEvaluations[selectedRegistrant?.registrant_id || ''] || []}
      />

      {showExportDataPopup && (
        <div className="popOverBackdrop"
        />)}
      <ExportRegistrantsPopup
        isOpen={showExportDataPopup}
        onClose={() => { setShowExportDataPopup(false); }}
        registrants={filteredRegistrants}
      />

      {showDeleteRegistrantPopup && (
        <div className="popOverBackdrop">
          <div style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', height: '100vh' }}>
            <div className='card' style={{ maxWidth: '600px', maxHeight: '500px', width: '90%' }}>
              <h2>
                Are you sure you want to delete{' '}
                {registrantToDelete?.first_name} {registrantToDelete?.last_name} and all
                of their data from this event?
              </h2>
              <h3>This action <u>cannot</u> be undone.</h3>
              <p>
                Please type the word <i>delete</i> to continue with permanent removal.
              </p>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'center' }}>
                <input
                  type='text'
                  onChange={handleConfirmationChange}
                  value={confirmationText}
                />
                <button
                  className='warningButton'
                  disabled={confirmationText !== 'delete'}
                  style={{ height: '40px' }}
                  onClick={() => { handleDeletePressed(registrantToDelete?.registrant_id); }}
                >
                  Confirm Delete
                </button>
              </div>
              <div className='buttonsRow' style={{ marginTop: '10px' }}>
                <button type='button' className='cancelButton' onClick={() => { setShowDeleteRegistrantPopup(false); setRegistrantToDelete(undefined); setConfirmationText(''); }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RegistrantList;
